import React from "react";
import { HelmetProps, Helmet } from "react-helmet";
import { RouteComponentProps } from "react-router-dom";
import { CognitoGroups } from "../../models/CognitoUser";
import { ReactNode } from "react";
import BaseApi from "./api/base-api";
import { BaseAvailableApis, RouteType, AbstractRouteType } from ".";
import { update as IntercomUpdate } from "../../components/IntercomLiveChat"

export type BuilderCallback = (
  context: BuilderContext,
  props: ChildRouteProps
) => RouteBuilder;

export type BuilderContext = {
  builder: RouteBuilder;
};

export type ChildRouteProps = {
  parent?: RouteType<any>;
};

export class RouteBuilder<A extends BaseAvailableApis = any>
  implements RouteType<A> {
  private parent?: RouteBuilder;
  public _protected?: boolean;
  public _appMigrationRequired?: boolean;
  public childRoutes: RouteBuilder[] = [];
  public path?: string;
  public render!: (props: RouteComponentProps) => ReactNode;
  public requiredRoles: CognitoGroups[] = [];
  public strict?: boolean;
  public exact?: boolean;
  public external?: boolean;
  public featureFlagEnvKey?: string;
  private apis: BaseApi<any, any, any>[] = [];
  private locked: boolean = false;
  private _seo?: HelmetProps;

  private constructor(parent?: RouteBuilder) {
    this.parent = parent;
  }

  public static create<A extends BaseAvailableApis>() {
    return new RouteBuilder<A>();
  }

  public addApi<S extends BaseApi<A>>(api: S) {
    api.onRouteBind(this);
    this.apis.push(api);
    return this;
  }

  /**
   * Gets an API by name
   * @param name
   */
  public getApi<Av extends BaseAvailableApis, S extends BaseApi<A>>(
    name: Av
  ): S | undefined {
    return this.apis.find((api) => (name === api.name ? api : null)) as S;
  }

  public setPath(path: string) {
    this.path = path;

    return this;
  }

  public protect() {
    this._protected = true;
    return this;
  }

  public appMigrationRequired() {
    this._appMigrationRequired = true;
    return this;
  }

  public setExact() {
    this.exact = true;
    return this;
  }

  public setRequiredRoles(roles: CognitoGroups[]) {
    this.requiredRoles = roles;
    return this;
  }

  public rolesMatch(roles: CognitoGroups[]): boolean {
    // If the roles passed in are undefined, then no roles are required
    if (!roles) return true;
    // Ensure that the cognito groups array and roles array match
    return this.requiredRoles.every((el) => roles.includes(el));
  }

  /**
   * Sets the function to be called when the route is rendered
   * Wrap with call to update the intercom bubble.
   * @param render
   */
  public onRender(
    render: (props: RouteComponentProps<any>) => React.ReactNode
  ) {
    this.render = render;
    return this;
  }

  /**
   * Allows you to pass SEO attributes which is then handled by Helmet.
   *
   * These will automatically be injected into your component tree when @render is called by react router
   *
   * @param context
   */
  public seo(seo: ((context: this) => HelmetProps) | HelmetProps) {
    if (typeof seo === "function") this._seo = seo(this);
    else this._seo = seo;
    return this;
  }

  /**
   *  Adds a child route to this route
   * @param build
   */
  public addChild(build: BuilderCallback) {
    this.childRoutes.push(
      build({ builder: new RouteBuilder(this) }, { parent: this })
    );
    return this;
  }

  /**
   * Builds this route
   */
  public build() {
    for (const child of this.childRoutes) child.build();
    console.debug(`Building route ${this.path}`);
    if (this._seo) {
      if (!this.render)
        throw new Error(
          `Can not build route ${this.path} with seo as there is nothing to render. Routes that have no view should not contain SEO attributes`
        );
      // Create copy of function
      const r = this.render.bind(this);
      this.render = (props) => {
        IntercomUpdate()
        return [React.createElement(Helmet, this._seo), r(props)];
      };
    }
    return this;
  }

  /**
   *  Set external flag to true, this will alter how the generated link is
   *  processed and will load it outside of the react router
   */
  public setExternal() {
    this.external = true;
    return this;
  }

  /**
   *  This route is restricted to environments with the corresponding Feature Flag enabled.
   *
   *  If a route has setFeatureRestricted called on it, then it will only be rendered if there
   *  is an environment variable of the same name present.
   */
  public setFeatureRestricted(featureFlag: string) {
    this.featureFlagEnvKey = `REACT_APP_${featureFlag.toUpperCase()}`;
    return this;
  }

  /**
   *  Returns true if menu item has featureFlagEnvKey and *no* matching Environment variable
   */
  public isFeatureRestricted(): boolean {
    if (!this.featureFlagEnvKey) return false;
    if (process.env[this.featureFlagEnvKey]) return false;
    return true;
  }

  private lock() {
    this.locked = true;
  }
}
